import {
  AdminUser,
  ListParams,
  ListResult,
  UserPermissions,
} from "../../types/admin";
import { supabase } from "../supabaseClient";

export interface DeletionRequestRow {
  id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  status: "PENDING" | "CONFIRMED" | "CANCELLED" | string;
  expires_at: string;
  created_at: string;
}

const USER_SELECT_QUERY = `
  id,
  created_at,
  email,
  full_name,
  role,
  client_type,
  subscription,
  last_login,
  permissions,
  projects(count)
`;

const mapSortField = (sortField: string | undefined): string => {
  if (!sortField) return "created_at";

  const map: Record<string, string> = {
    createdAt: "created_at",
  };

  return map[sortField] || sortField;
};

export async function listUsers(
  params: ListParams = {},
): Promise<ListResult<AdminUser>> {
  const page = params.page || 1;
  const pageSize = params.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;

  let query = supabase
    .from("profiles")
    .select(USER_SELECT_QUERY, { count: "exact" });

  if (params.search) {
    const searchPattern = `%${params.search || ""}%`;
    query = query.or(
      `email.ilike.${searchPattern},full_name.ilike.${searchPattern}`,
    );
  }

  const dbSortBy = mapSortField(params.sort);
  const ascending = params.dir !== "desc";

  query = query.order(dbSortBy, { ascending: ascending, nullsFirst: false });

  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error("Supabase query error:", error);
    throw new Error("Failed to fetch users from the database.");
  }

  const rows: AdminUser[] = data.map((item) => ({
    id: item.id,
    email: item.email,
    full_name: item.full_name,
    role: item.role,
    subscription: item.subscription,
    createdAt: item.created_at,
    client_type: item.client_type,
    dealsCount: item.projects?.[0]?.count || 0,
    last_login: item.last_login || null,
    permissions: (item.permissions as UserPermissions) || {},
  }));

  const total = count || 0;

  return {
    rows,
    total,
    page,
    pageSize,
    totalPages: Math.ceil(total / pageSize),
  };
}

export async function getUser(id: string): Promise<AdminUser | null> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select(USER_SELECT_QUERY)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("Error fetching user:", error);
      throw new Error(`Failed to fetch user: ${error.message}`);
    }

    if (!data) {
      return null;
    }

    const user: AdminUser = {
      id: data.id,
      email: data.email,
      full_name: data.full_name,
      role: data.role,
      subscription: data.subscription,
      createdAt: data.created_at,
      client_type: data.client_type,
      last_login: data.last_login || null,
      permissions: (data.permissions as UserPermissions) || {},
    };

    return user;
  } catch (error) {
    console.error("Error in getUser:", error);
    throw error;
  }
}

export async function updateUser(
  id: string,
  payload: Partial<AdminUser>,
): Promise<AdminUser> {
  try {
    const updateData: Record<string, any> = {};

    if (payload.full_name !== undefined) {
      updateData.full_name = payload.full_name;
    }
    if (payload.role !== undefined) {
      updateData.role = payload.role;
    }
    if (payload.subscription !== undefined) {
      updateData.subscription = payload.subscription;
    }
    if (payload.client_type !== undefined) {
      updateData.client_type = payload.client_type;
    }
    if (payload.permissions !== undefined) {
      updateData.permissions = payload.permissions;
    }

    if (Object.keys(updateData).length === 0) {
      throw new Error("No valid fields provided for update");
    }

    const { data, error } = await supabase
      .from("profiles")
      .update(updateData)
      .eq("id", id)
      .select(USER_SELECT_QUERY)
      .maybeSingle();

    if (error) {
      console.error("Error updating user:", error);
      throw new Error(`Failed to update user: ${error.message}`);
    }

    if (!data) {
      throw new Error("User not found after update");
    }

    const updatedUser: AdminUser = {
      id: data.id,
      email: data.email,
      full_name: data.full_name,
      role: data.role,
      subscription: data.subscription,
      createdAt: data.created_at,
      client_type: data.client_type,
      last_login: data.last_login || null,
      permissions: (data.permissions as UserPermissions) || {},
    };

    return updatedUser;
  } catch (error) {
    console.error("Error in updateUser:", error);
    throw error;
  }
}

/**
 * Update only the permissions field for a user.
 * Merges existing permissions with the new ones.
 */
export async function updateUserPermissions(
  id: string,
  permissions: UserPermissions,
): Promise<AdminUser> {
  try {
    // Fetch current permissions first to merge
    const current = await getUser(id);
    if (!current) throw new Error("User not found");

    const merged: UserPermissions = {
      ...(current.permissions || {}),
      ...permissions,
    };

    return updateUser(id, { permissions: merged });
  } catch (error) {
    console.error("Error in updateUserPermissions:", error);
    throw error;
  }
}

const DELETION_REQUEST_SELECT_QUERY =
  "id, user_id, status, expires_at, created_at, profiles!inner(email, full_name)";

export async function listDeletionRequests(
  params: ListParams = {},
): Promise<ListResult<DeletionRequestRow>> {
  const page = params.page || 1;
  const pageSize = params.pageSize || 25;
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;

  let query = supabase
    .from("deletion_requests")
    .select(DELETION_REQUEST_SELECT_QUERY, { count: "exact" });

  if (params.search) {
    const searchPattern = `%${params.search || ""}%`;

    query = query.or(
      `email.ilike.${searchPattern},full_name.ilike.${searchPattern}`,
      { referencedTable: "profiles" },
    );
  }

  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error("Supabase query error:", error);
    throw new Error("Failed to fetch deletion requests from the database.");
  }

  const rows: DeletionRequestRow[] = data.map((item: any) => ({
    id: item.id,
    user_id: item.user_id,
    email: item.profiles?.email || null,
    full_name: item.profiles?.full_name || null,
    status: item.status,
    expires_at: item.expires_at,
    created_at: item.created_at,
  }));

  const total = count || 0;

  return {
    rows,
    total,
    page,
    pageSize,
    totalPages: Math.ceil(total / pageSize),
  };
}
